/**
 * FranchiseSettlementTrackingPage — `/franchise/settlements`.
 *
 * Wraps the `/v1/franchise/settlements` controller. The list endpoint is
 * per-franchise, so the page picks one from `GET /v1/franchise/franchises`,
 * shows its settlement runs, and exposes the daily-pass admin action via
 * `POST /v1/franchise/settlements/run`.
 *
 * Status flow: PENDING → INITIATED → COMPLETED | FAILED.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  listFranchises,
  listSettlementsForFranchise,
  runDailySettlement,
  type SettlementRunStatus,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

const STATUSES: readonly SettlementRunStatus[] = ['PENDING', 'INITIATED', 'COMPLETED', 'FAILED'];

function formatNgn(decimal: string): string {
  const n = Number.parseFloat(decimal);
  if (!Number.isFinite(n)) return `₦${decimal}`;
  return `₦${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

export function FranchiseSettlementTrackingPage(): JSX.Element {
  const qc = useQueryClient();
  const [franchiseId, setFranchiseId] = useState('');
  const [statusFilter, setStatusFilter] = useState<SettlementRunStatus | ''>('');
  const [runDate, setRunDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const franchises = useQuery({
    queryKey: ['franchise', 'settlement', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const runs = useQuery({
    queryKey: ['franchise', 'settlement', 'runs', franchiseId, statusFilter],
    queryFn: () =>
      listSettlementsForFranchise(franchiseId, {
        pageSize: 50,
        ...(statusFilter === '' ? {} : { status: statusFilter as SettlementRunStatus }),
      }),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const runDaily = useMutation({
    mutationFn: () => runDailySettlement(runDate),
    onSuccess: (r) => {
      setActionErr(null);
      setActionMsg(`Started ${r.runs.length} run(s); ${r.skipped} skipped.`);
      void qc.invalidateQueries({ queryKey: ['franchise', 'settlement', 'runs'] });
    },
    onError: (e: Error) => {
      setActionMsg(null);
      setActionErr(e.message);
    },
  });

  const denied =
    runs.isError && runs.error instanceof ApiError &&
    (runs.error.status === 401 || runs.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settlement tracking"
        description="Daily settlement runs against a franchise."
      />

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Franchise</label>
          {franchises.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : (
            <select
              value={franchiseId}
              onChange={(e) => setFranchiseId(e.target.value)}
              className="px-3 py-2 border border-border rounded-lg text-sm min-w-[260px]"
            >
              <option value="">Select…</option>
              {franchises.data?.items.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.legalName} ({f.franchiseNumber})
                </option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as SettlementRunStatus | '')}
            className="px-3 py-2 border border-border rounded-lg text-sm"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1" />
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Run date</label>
          <input
            type="date"
            value={runDate}
            onChange={(e) => setRunDate(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => runDaily.mutate()}
          disabled={runDaily.isPending}
          className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
        >
          {runDaily.isPending ? 'Running…' : 'Run daily pass'}
        </button>
      </div>

      {actionMsg ? <p className="text-sm text-success">{actionMsg}</p> : null}
      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}

      {franchiseId.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Pick a franchise to view its settlement runs.
        </p>
      ) : runs.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseSettlement/read</code> required.
        </DeferredNotice>
      ) : runs.isError ? (
        <DeferredNotice title="Service unreachable">
          {(runs.error as Error).message}
        </DeferredNotice>
      ) : (runs.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No settlement runs match the filter.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Period</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Revenue</th>
                <th className="text-right px-4 py-2">Commission</th>
                <th className="text-right px-4 py-2">Net remitted</th>
                <th className="text-left px-4 py-2">Initiated</th>
                <th className="text-left px-4 py-2">Completed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {runs.data?.items.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs text-muted">
                    {r.fromDate} → {r.toDate}
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {formatNgn(r.totalRevenue)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {formatNgn(r.totalCommission)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {formatNgn(r.netRemitted)}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {new Date(r.initiatedAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {r.completedAt ? new Date(r.completedAt).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
